import React, { useState, useEffect } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';
import BillingSection from '../../components/BillingSection.jsx';

export default function Billing({
  facility,
  facility_licenses = [],
  facility_users = [],
  invoices = [],
  subscriptions = [],
}) {
  const [purchaseConfirmed, setPurchaseConfirmed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('purchased') === '1';
  });

  useEffect(() => {
    if (!purchaseConfirmed || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('purchased');
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('setup_intent');
    url.searchParams.delete('setup_intent_client_secret');
    url.searchParams.delete('redirect_status');
    window.history.replaceState({}, '', url.toString());
  }, [purchaseConfirmed]);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState(null);
  const [assignUserName, setAssignUserName] = useState('');
  const [assignLicenseId, setAssignLicenseId] = useState('');

  const assignableLicenses = facility_licenses.filter(fl => fl.available);

  const openAssignModal = (user) => {
    setAssignUserId(user.id);
    setAssignUserName(user.display_name);
    setAssignLicenseId('');
    setShowAssignModal(true);
  };

  const handleAssign = () => {
    if (!assignLicenseId) return;
    router.post(`/facilities/${facility.id}/billing/assign_license`, {
      user_id: assignUserId,
      license_id: assignLicenseId,
    }, {
      onSuccess: () => {
        setShowAssignModal(false);
        setAssignUserId(null);
        setAssignLicenseId('');
      },
    });
  };

  const handleCancelSubscription = (subscriptionId) => {
    router.post(`/facilities/${facility.id}/billing/subscriptions/cancel`,
      { subscription_id: subscriptionId });
  };

  const handleResumeSubscription = (subscriptionId) => {
    router.post(`/facilities/${facility.id}/billing/subscriptions/resume`,
      { subscription_id: subscriptionId });
  };

  const initials = (name) => name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Billing</h1>
          <p className="mt-1 text-sm text-gray-500">Manage training seats, subscriptions, and invoices.</p>
        </div>

        {purchaseConfirmed && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3">
            <div className="w-8 h-8 bg-white text-emerald-600 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold">Payment received.</p>
              <p className="text-emerald-700 mt-0.5">Your training seats will appear here shortly. Refresh if you don't see them in a few seconds.</p>
            </div>
            <button onClick={() => setPurchaseConfirmed(false)} className="text-emerald-700 hover:text-emerald-900 cursor-pointer">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {/* Training Seats */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Training Seats</h2>
            <Link href={`/facilities/${facility.id}/billing/purchase`}
              className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
              Purchase
            </Link>
          </div>

          {facility_licenses.length > 0 ? (
            <div className="space-y-3">
              {facility_licenses.map(fl => {
                const available = fl.quantity_purchased - fl.quantity_assigned;
                const pct = fl.quantity_purchased > 0 ? Math.round((fl.quantity_assigned / fl.quantity_purchased) * 100) : 0;
                return (
                  <div key={fl.id} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="font-semibold text-gray-900">{fl.license_name}</p>
                      {available > 0 && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {available} available
                        </span>
                      )}
                    </div>
                    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-gray-500">{fl.quantity_assigned} of {fl.quantity_purchased} assigned</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-600">No training seats purchased yet.</p>
              <p className="text-sm text-gray-500 mt-1">Click "Purchase" to get started.</p>
            </div>
          )}
        </div>

        {/* Users / Team */}
        {facility_users.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <span className="ml-auto text-sm text-gray-400">{facility_users.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {facility_users.map(user => (
                <div key={user.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {initials(user.display_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{user.display_name}</p>
                        {user.status === 'invited' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">Invited</span>
                        )}
                        {user.has_active_license && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {user.active_license_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{user.email} <span className="capitalize">· {user.role}</span></p>
                    </div>
                  </div>
                  {!user.has_active_license && (
                    assignableLicenses.length > 0 ? (
                      <button onClick={() => openAssignModal(user)}
                        className="shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm transition cursor-pointer">
                        Assign Training
                      </button>
                    ) : (
                      <p className="text-sm text-gray-400 italic shrink-0">No seats available</p>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <BillingSection
          subscriptions={subscriptions}
          invoices={invoices}
          onCancel={handleCancelSubscription}
          onResume={handleResumeSubscription}
        />
      </div>

      {showAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setShowAssignModal(false)} />
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Assign Training</h3>
                </div>
                <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Assign training to <span className="font-semibold text-gray-900">{assignUserName}</span>
              </p>
              <div className="space-y-2 mb-5">
                {assignableLicenses.map(fl => (
                  <label key={fl.id} className={`flex items-center cursor-pointer p-3 border-2 rounded-xl transition-colors ${assignLicenseId === String(fl.license_id) ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`}>
                    <input type="radio" name="assign_license" value={fl.license_id} checked={assignLicenseId === String(fl.license_id)}
                      onChange={() => setAssignLicenseId(String(fl.license_id))} className="h-4 w-4 text-indigo-600" />
                    <div className="ml-3">
                      <p className="font-semibold text-gray-900 text-sm">{fl.license_name}</p>
                      <p className="text-xs text-gray-500">{fl.quantity_purchased - fl.quantity_assigned} available</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button type="button" onClick={() => setShowAssignModal(false)}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer transition">
                  Cancel
                </button>
                <button onClick={handleAssign} disabled={!assignLicenseId}
                  className="flex-1 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer disabled:opacity-50 shadow-sm transition">
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
